import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const formatDateTime = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-';
  // Formatta come italiano con separatore migliaia sempre presente
  // Intl.NumberFormat non aggiunge il punto per numeri < 10.000, quindi usiamo toFixed + replace
  let num = Number(amount);
  if (isNaN(num)) return '-';
  // Normalizza signed-zero: se arrotondato a 2 decimali è 0, usa 0 positivo (evita "-0,00 €")
  if (Math.round(num * 100) === 0) num = 0;
  // Formato: 1.234,56 €
  const formatted = num.toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formatted} €`;
};

// True se l'importo, arrotondato a 2 decimali monetari, è zero (gestisce signed-zero/floating point)
export const isZeroMoney = (amount) => {
  const n = Number(amount);
  return !isNaN(n) && Math.round(n * 100) === 0;
};

// Prefisso di segno per importi: '' se zero (arrotondato), '−' se negativo, '+' se positivo (solo se plus=true)
export const moneySign = (amount, { plus = true } = {}) => {
  const n = Number(amount);
  if (isNaN(n) || Math.round(n * 100) === 0) return '';
  return n < 0 ? '−' : (plus ? '+' : '');
};

/**
 * Parse Italian currency format to number
 * Accepts: "55.338,00", "55338,00", "55338.00", "55338"
 * Returns: 55338.00 (number)
 */
export const parseItalianCurrency = (value) => {
  if (value === null || value === undefined || value === '') return null;
  
  // Convert to string
  let str = String(value).trim();
  
  // Remove € symbol and spaces
  str = str.replace(/€/g, '').replace(/\s/g, '');
  
  // If empty after cleanup, return null
  if (!str) return null;
  
  // Count dots and commas to determine format
  const dots = (str.match(/\./g) || []).length;
  const commas = (str.match(/,/g) || []).length;
  
  // Italian format: "55.338,00" (dot as thousand separator, comma as decimal)
  // American format: "55338.00" (dot as decimal)
  
  if (commas === 1 && dots >= 1) {
    // Italian format with thousand separators: "55.338,00"
    str = str.replace(/\./g, ''); // Remove thousand separators
    str = str.replace(',', '.'); // Convert decimal comma to dot
  } else if (commas === 1 && dots === 0) {
    // Italian format without thousand separators: "55338,00"
    str = str.replace(',', '.'); // Convert decimal comma to dot
  } else if (commas === 0 && dots === 1) {
    // American format: "55338.00" - keep as is
  } else if (commas === 0 && dots === 0) {
    // Integer: "55338" - keep as is
  } else if (commas === 0 && dots > 1) {
    // Italian format with only thousand separators: "55.338"
    str = str.replace(/\./g, ''); // Remove thousand separators
  } else {
    // Invalid format
    return null;
  }
  
  // Parse to float
  const num = parseFloat(str);
  
  // Return null if NaN
  if (isNaN(num)) return null;
  
  // Round to 2 decimal places
  return Math.round(num * 100) / 100;
};

/**
 * Format number for Italian currency input display
 * Input: 55338.00 (number)
 * Output: "55338,00" (string for display)
 */
export const formatCurrencyInput = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return '';
  return num.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatPercentage = (value, decimals = 0) => {
  if (value === null || value === undefined) return '-';
  if (decimals > 0) {
    return `${Number(value).toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
  }
  return `${Math.round(value)}%`;
};

// Lead Status Labels
export const leadStatusLabels = {
  nuovo: 'Nuovo',
  contattato: 'Contattato',
  non_risponde: 'Non risponde',
  assegnato: 'Assegnato',
  appuntamento_fissato: 'Appuntamento fissato',
  sopralluogo_effettuato: 'Sopralluogo effettuato',
  in_preventivazione: 'In preventivazione',
  preventivo_presentato: 'Preventivo presentato',
  in_trattativa: 'In trattativa',
  in_contratto: 'In contratto',
  perso: 'Perso',
  convertito: 'Convertito'
};

export const leadStatusColors = {
  nuovo: 'info',
  contattato: 'pending',
  non_risponde: 'warning',
  assegnato: 'info',
  appuntamento_fissato: 'pending',
  sopralluogo_effettuato: 'success',
  in_preventivazione: 'pending',
  preventivo_presentato: 'info',
  in_trattativa: 'warning',
  in_contratto: 'pending',
  perso: 'error',
  convertito: 'success'
};

// Appointment Status Labels
export const appointmentStatusLabels = {
  assegnato: 'Assegnato',
  da_confermare: 'Da confermare',
  confermato: 'Confermato',
  contestato: 'Contestato',
  effettuato: 'Effettuato',
  annullato: 'Annullato',
  no_show: 'No show',
  rifiutato_dal_cliente: 'Rifiutato'
};

export const appointmentStatusColors = {
  assegnato: 'info',
  da_confermare: 'pending',
  confermato: 'success',
  contestato: 'warning',
  effettuato: 'success',
  annullato: 'error',
  no_show: 'error',
  rifiutato_dal_cliente: 'error'
};

// Lead Opportunity Type Labels
export const leadOpportunityTypeLabels = {
  non_definito: 'Non definito',
  lavori: 'Lavori',
  arredi: 'Arredi',
  lavori_arredi: 'Lavori + Arredi'
};

export const leadOpportunityTypeColors = {
  non_definito: 'neutral',
  lavori: 'info',
  arredi: 'success',
  lavori_arredi: 'warning'
};

// Estimate Status Labels
export const estimateStatusLabels = {
  in_bozza: 'Bozza',
  in_compilazione: 'In compilazione',
  appuntamento_in_sede_pianificato: 'Appuntamento in sede',
  firmato: 'Firmato',
  rifiutato: 'Rifiutato',
  in_attesa_validazione_admin: 'In validazione',
  validato: 'Validato',
  convertito: 'Convertito'
};

export const estimateStatusColors = {
  in_bozza: 'neutral',
  in_compilazione: 'pending',
  appuntamento_in_sede_pianificato: 'info',
  firmato: 'success',
  rifiutato: 'error',
  in_attesa_validazione_admin: 'warning',
  validato: 'success',
  convertito: 'success'
};

// Project Status Labels
export const projectStatusLabels = {
  in_attesa_acconto: 'Attesa acconto',
  acconto_da_fatturare: 'Da fatturare',
  acconto_pagato: 'Acconto pagato',
  in_attesa_scelte: 'Attesa scelte materiali',
  scelte_completate: 'Scelte completate',
  in_attesa_materiali: 'Attesa materiali',
  pronto_partenza: 'Pronto partenza',
  cantiere_avviato: 'Cantiere avviato',
  in_corso: 'In corso',
  sospeso: 'Sospeso',
  completato: 'Completato',
  chiuso: 'Chiuso'
};

export const projectStatusColors = {
  in_attesa_acconto: 'pending',
  acconto_da_fatturare: 'warning',
  acconto_pagato: 'info',
  in_attesa_scelte: 'pending',
  scelte_completate: 'info',
  in_attesa_materiali: 'pending',
  pronto_partenza: 'success',
  cantiere_avviato: 'success',
  in_corso: 'success',
  sospeso: 'error',
  completato: 'success',
  chiuso: 'neutral'
};

// Payment Status Labels
export const paymentStatusLabels = {
  da_fatturare: 'Da fatturare',
  richiesta_fattura_inviata: 'Richiesta inviata',
  fattura_emessa_esternamente: 'Fattura emessa',
  in_attesa_pagamento: 'Attesa pagamento',
  pagamento_ricevuto: 'Pagato',
  scaduto: 'Scaduto',
  sollecito_richiesto: 'Sollecito'
};

export const paymentStatusColors = {
  da_fatturare: 'pending',
  richiesta_fattura_inviata: 'info',
  fattura_emessa_esternamente: 'info',
  in_attesa_pagamento: 'warning',
  pagamento_ricevuto: 'success',
  scaduto: 'error',
  sollecito_richiesto: 'error'
};

// Milestone Status Labels (nuovo sistema fatturazione basata su SAL)
export const milestoneStatusLabels = {
  non_sbloccato: 'Non sbloccato',
  da_fatturare: 'Da fatturare',
  fatturato: 'Fatturato',
  incassato: 'Incassato',
  scaduto: 'Scaduto'
};

export const milestoneStatusColors = {
  non_sbloccato: 'neutral',
  da_fatturare: 'warning',
  fatturato: 'info',
  incassato: 'success',
  scaduto: 'error'
};

// Suspension Status Labels
export const suspensionStatusLabels = {
  richiesta_inviata: 'Richiesta inviata',
  in_approvazione: 'In approvazione',
  approvata: 'Approvata',
  respinta: 'Respinta',
  conclusa: 'Conclusa'
};

export const suspensionStatusColors = {
  richiesta_inviata: 'pending',
  in_approvazione: 'warning',
  approvata: 'success',
  respinta: 'error',
  conclusa: 'neutral'
};

// Extra Status Labels
export const extraStatusLabels = {
  // Nuovo flusso
  richiesto: 'Richiesto',
  in_preparazione: 'In Preparazione',
  pronto: 'Pronto',
  modifica_richiesta: 'Modifica Richiesta',
  in_attesa_firma: 'In Attesa Firma',
  firmato_cliente: 'Firmato Cliente',
  in_validazione: 'In Validazione',
  validato: 'Validato',
  in_attesa_ordine: 'In Attesa Ordine',
  ordinato: 'Ordinato',
  completato: 'Completato',
  rifiutato: 'Rifiutato',
  // Vecchi stati per retrocompatibilità
  bozza: 'In Preparazione',
  in_attesa: 'In Preparazione',
  approvato_admin: 'Validato',
  accettato_cliente: 'Firmato Cliente',
  in_attesa_approvazione: 'In Validazione',
  approvato: 'Validato'
};

export const extraStatusColors = {
  // Nuovo flusso
  richiesto: 'secondary',
  in_preparazione: 'info',
  pronto: 'warning',
  modifica_richiesta: 'error',
  in_attesa_firma: 'warning',
  firmato_cliente: 'info',
  in_validazione: 'info',
  validato: 'success',
  in_attesa_ordine: 'warning',
  ordinato: 'info',
  completato: 'success',
  rifiutato: 'error',
  // Vecchi stati
  bozza: 'info',
  in_attesa: 'info',
  approvato_admin: 'success',
  accettato_cliente: 'info',
  in_attesa_approvazione: 'info',
  approvato: 'success'
};

// Estimate Sections Labels
export const estimateSectionLabels = {
  lavori_edili: 'Lavori Edili',
  lavori_idraulici: 'Lavori Idraulici',
  lavori_elettrici: 'Lavori Elettrici',
  forniture_pavimentazioni: 'Forniture Pavimentazioni',
  forniture_porte_interne: 'Forniture Porte Interne',
  forniture_infissi: 'Forniture Infissi',
  forniture_climatizzazione: 'Forniture Climatizzazione',
  forniture_porte_blindate: 'Forniture Porte Blindate',
  forniture_varie: 'Forniture Varie'
};

// Role Labels
export const roleLabels = {
  admin: 'Amministratore',
  administration: 'Amministrazione',
  project_manager: 'Project Manager',
  external_technician: 'Tecnico Esterno',
  furniture_sales: 'Venditore Arredi'
};

// Furniture Quote Status Labels
export const furnitureQuoteStatusLabels = {
  bozza: 'Bozza',
  presentato: 'Presentato',
  accettato: 'Confermato',  // Legacy alias
  confermato: 'Confermato',
  ordinato: 'Ordinato',
  merce_pronta: 'Merce pronta',
  consegnato: 'Consegnato',
  chiuso: 'Chiuso',
  annullato: 'Annullato'
};

export const furnitureQuoteStatusColors = {
  bozza: 'neutral',
  presentato: 'info',
  accettato: 'success',  // Legacy alias
  confermato: 'success',
  ordinato: 'warning',
  merce_pronta: 'pending',
  consegnato: 'info',
  chiuso: 'success',
  annullato: 'error'
};

// Furniture Payment Status Labels
export const furniturePaymentStatusLabels = {
  da_pagare: 'Da pagare',
  non_pagato: 'Da pagare',  // Legacy alias
  acconto_pagato: 'Acconto pagato (50%)',
  saldo_pagato: 'Saldo pagato (100%)'
};

export const furniturePaymentStatusColors = {
  da_pagare: 'warning',
  non_pagato: 'warning',  // Legacy alias
  acconto_pagato: 'info',
  saldo_pagato: 'success'
};

// Get badge class based on status type
export const getBadgeClass = (type) => {
  const classes = {
    success: 'badge-success',
    warning: 'badge-warning',
    error: 'badge-error',
    info: 'badge-info',
    pending: 'badge-pending',
    neutral: 'badge-neutral'
  };
  return classes[type] || classes.neutral;
};
